import { useIonRouter } from "@ionic/react";
import queryString from "query-string";
import { z } from "zod";

/** Based on https://www.flightcontrol.dev/blog/fix-nextjs-routing-to-have-full-type-safety */

type RouteBuilder<Params extends z.ZodSchema, Search extends z.ZodSchema> = {
  (p?: z.input<Params>, options?: { search?: z.input<Search> }): string;
  parse: (input: z.input<Params>) => z.output<Params>;
  useParams: () => z.output<Params>;
  useSearchParams: () => z.output<Search>;
  params: z.output<Params>;
  pattern: string;
};

const empty: z.ZodSchema = z.object({});

export function makeRoute<Params extends z.ZodSchema, Search extends z.ZodSchema>(
  path: string,
  paramsSchema: Params = empty as Params,
  searchSchema: Search = empty as Search,
): RouteBuilder<Params, Search> {
  const routeBuilder: RouteBuilder<Params, Search> = (params, options) => {
    // Start with the path
    let resultPath = path;

    if (params) {
      // Replace path parameters first
      Object.entries(params).forEach(([key, value]) => {
        resultPath = resultPath.replace(`:${key}`, value);
      });
    }

    const searchString = options?.search && queryString.stringify(options.search);

    return [resultPath, searchString ? `?${searchString}` : ""].join("");
  };

  routeBuilder.useParams = function useParams(): z.output<Params> {
    const router = useIonRouter();
    const pathname = router.routeInfo.pathname;
    const pathSegments = pathname.split("/").filter(Boolean); // filter removes empty strings
    const routeSegments = path.split("/").filter(Boolean);

    const params: Record<string, string> = {};

    routeSegments.forEach((segment, index) => {
      if (segment.startsWith(":")) {
        const paramName = segment.slice(1).replace("?", ""); // Remove : and optional ?
        if (pathSegments[index]) {
          params[paramName] = pathSegments[index];
        }
      }
    });

    const result = paramsSchema.safeParse(params);
    if (!result.success) {
      throw new Error(
        `Invalid route params for route ${path}: ${JSON.stringify(result.error.errors, null, 2)}`,
      );
    }

    return result.data;
  };

  routeBuilder.useSearchParams = function useSearchParams(): z.output<Search> {
    const router = useIonRouter();
    const params = queryString.parse(router.routeInfo.search || window.location.search);

    const result = searchSchema.safeParse(params);
    if (!result.success) {
      throw new Error(
        `Invalid route search params for route ${path}: ${JSON.stringify(result.error.errors, null, 2)}`,
      );
    }

    return result.data;
  };

  routeBuilder.parse = function parse(args: z.input<Params>): z.output<Params> {
    const result = paramsSchema.safeParse(args);
    if (!result.success) {
      throw new Error(
        `Invalid route params for route ${path}: ${JSON.stringify(result.error.errors, null, 2)}`,
      );
    }
    return result.data;
  };

  routeBuilder.params = undefined as z.output<Params>;
  routeBuilder.pattern = path;

  // set the runtime getter
  Object.defineProperty(routeBuilder, "params", {
    get() {
      throw new Error(
        "Routes.[route].params is only for type usage, not runtime. Use it like `typeof Routes.[routes].params`",
      );
    },
  });

  return routeBuilder;
}

export function convertURLSearchParamsToObject(
  params: Readonly<URLSearchParams> | null,
): Record<string, string | string[]> {
  if (!params) {
    return {};
  }

  const obj: Record<string, string | string[]> = {};
  for (const [key, value] of params.entries()) {
    if (params.getAll(key).length > 1) {
      obj[key] = params.getAll(key);
    } else {
      obj[key] = value;
    }
  }
  return obj;
}
